<template>
  <div class="new-map-page">
    <div class="new-map-header">请选择要进入的工程</div>
    <div class="new-map-footer">
      <img :src="img('footer.png')" alt="">
    </div>

    <div class="status-panel">
      <div class="sp-header">
        <div class="sp-title">
          <img :src="img('1.png')" alt="">
          项目状态
        </div>
        <div class="sp-stripe"><img :src="img('2.png')" alt=""></div>
      </div>
      <img class="sp-divider" :src="img('6.png')" alt="">
      <div class="sp-cards">
        <div class="sp-card">
          <img :src="img('3.png')" alt="">
          <span>进行中({{ stateCounts.running }})</span>
        </div>
        <div class="sp-card">
          <img :src="img('5.png')" alt="">
          <span>未开始({{ stateCounts.pending }})</span>
        </div>
        <div class="sp-card">
          <img :src="img('4.png')" alt="">
          <span>已结束({{ stateCounts.done }})</span>
        </div>
      </div>
    </div>

    <div ref="mapContainer" class="map-container" :class="{ dragging: isDragging }" @wheel.prevent="onWheel"
      @mousedown="onMouseDown" @mousemove="onStitchMouseMove" @mouseleave="onStitchMouseLeave" @click="onMapClick">
      <div ref="mapWorld" class="map-world" :style="worldStyle">
        <img ref="bgImage" class="bg-image" :src="img('m.png')" alt="" @load="onBgLoad">
        <div ref="stitchLayer" class="stitch-layer" :style="layerStyle">
          <img v-for="id in stitchIds" :key="'stitch-' + id" :id="'stitch-' + id" :data-prov-id="id" class="stitch-img"
            :class="{
              hover: stitchHoverId === id && markerHoverProvId !== id,
              active: markerHoverProvId === id
            }" :src="img('oo-' + id + '.png')" :style="stitchStyles[id]" alt="" @load="onStitchImageLoad($event, id)">
        </div>
        <div ref="markersLayer" class="markers-layer" :style="layerStyle">
          <div v-for="marker in markerViews" :key="marker.key" class="map-marker"
            :style="[marker.style, { '--pulse-color': marker.pulseColor, '--inv-zoom': invZoom }]"
            @mouseenter="onMarkerEnter(marker, $event)" @mouseleave="onMarkerLeave" @click.stop="onMarkerClick(marker)">
            <img :src="marker.iconSrc" alt="">
            <div class="pulse-wrapper">
              <div class="pulse-ring"></div>
              <div class="pulse-ring"></div>
              <div class="pulse-ring"></div>
              <div class="pulse-dot"></div>
            </div>
            <div class="m-label-bottom">{{ marker.name }}</div>
          </div>
        </div>
      </div>

      <div v-if="infoVisible" ref="markerInfo" class="marker-info" :style="infoStyle">
        <div class="mi-title">{{ infoData.name }}</div>
        <div class="mi-body">
          <div class="mi-gauge">
            <div class="mi-gauge-wrap">
              <div ref="gaugeChart" class="mi-gauge-ec"></div>
              <div class="mi-gauge-val">{{ infoData.progress }}</div>
            </div>
            <div class="mi-gauge-label">总体进度(%)</div>
          </div>
          <div class="mi-stats">
            <div class="mi-stat-row">
              <div class="mi-stat">
                <img :src="img('12.png')" alt="">
                <div>
                  <span class="mi-stat-val orange">{{ infoData.doneLen }}</span>
                  <span class="mi-stat-desc">完成长度(m)</span>
                </div>
              </div>
              <div class="mi-stat">
                <img :src="img('13.png')" alt="">
                <div>
                  <span class="mi-stat-val green">{{ infoData.doneM2 }}</span>
                  <span class="mi-stat-desc">完成平方(m²)</span>
                </div>
              </div>
            </div>
            <div class="mi-stat-row">
              <div class="mi-stat">
                <img :src="img('14.png')" alt="">
                <div>
                  <span class="mi-stat-val blue">{{ infoData.grout }}</span>
                  <span class="mi-stat-desc">总注浆量(m³)</span>
                </div>
              </div>
              <div class="mi-stat">
                <img :src="img('15.png')" alt="">
                <div>
                  <span class="mi-stat-val purple">{{ infoData.ash }}</span>
                  <span class="mi-stat-desc">总注灰量(t)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts"
import { getProjectList, setProjectList } from "@/api/login"
import {
  IMG_BASE,
  MAP_W,
  MAP_H,
  PULSE_COLORS,
  stitchData,
} from "@/utils/shareMap/mapConfig"
import {
  createHomography,
  findProvinceByLngLat,
  getDisplayRect,
  getStitchStyle,
  lngLatToPercent,
  stateToIcon,
} from "@/utils/shareMap/mapMath"

const INFO_ARC_STOPS = [
  { pos: 0, r: 0x00, g: 0x99, b: 0xff },
  { pos: 0.33, r: 0x00, g: 0xd2, b: 0xff },
  { pos: 0.66, r: 0x01, g: 0xf7, b: 0x8a },
  { pos: 1, r: 0x0f, g: 0xe5, b: 0x58 },
]

export default {
  name: "NewMap",
  data() {
    const mapCtx = createHomography()
    return {
      homography: mapCtx.homography,
      calByProvince: mapCtx.calByProvince,
      points: [],
      stitchIds: Object.keys(stitchData),
      stitchStyles: {},
      displayRect: { x: 0, y: 0, w: 1, h: 1 },
      mapZoom: 1,
      mapPanX: 0,
      mapPanY: 0,
      minZoom: 0.7,
      maxZoom: 5,
      isDragging: false,
      didDrag: false,
      dragStartX: 0,
      dragStartY: 0,
      panStartX: 0,
      panStartY: 0,
      stitchHoverId: null,
      markerHoverProvId: null,
      stitchAlphaData: {},
      alphaLoadFailed: false,
      hoveredMarkerKey: null,
      infoVisible: false,
      infoStyle: {},
      infoData: {},
      infoGaugeChart: null,
      stateCounts: { running: 0, pending: 0, done: 0 },
    }
  },
  computed: {
    worldStyle() {
      return {
        transform: `translate(${this.mapPanX}px, ${this.mapPanY}px) scale(${this.mapZoom})`,
      }
    },
    layerStyle() {
      const r = this.displayRect
      return {
        left: `${r.x}px`,
        top: `${r.y}px`,
        width: `${r.w}px`,
        height: `${r.h}px`,
      }
    },
    invZoom() {
      return 1 / this.mapZoom
    },
    markerViews() {
      return this.points
        .map((p) => {
          const pos = lngLatToPercent(
            this.homography,
            this.calByProvince,
            p.lng,
            p.lat,
            p.provId
          )
          if (!pos) return null
          const icon =
            this.hoveredMarkerKey === p.id ? "yellow" : p.icon
          return {
            key: p.id,
            point: p,
            name: p.name,
            style: {
              left: `${pos.px}%`,
              top: `${pos.py}%`,
            },
            iconSrc: this.img(`${icon}.png`),
            pulseColor:
              this.hoveredMarkerKey === p.id
                ? "#FF570B"
                : PULSE_COLORS[p.icon] || PULSE_COLORS.blue,
          }
        })
        .filter(Boolean)
    },
  },
  mounted() {
    this.stitchIds.forEach((id) => {
      this.$set(this.stitchStyles, id, getStitchStyle(id))
    })
    this.loadProjects()
    window.addEventListener("mousemove", this.onMouseMove)
    window.addEventListener("mouseup", this.onMouseUp)
    window.addEventListener("resize", this.syncLayers)
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onMouseMove)
    window.removeEventListener("mouseup", this.onMouseUp)
    window.removeEventListener("resize", this.syncLayers)
    this.disposeGauge()
  },
  methods: {
    img(name) {
      return `${IMG_BASE}/${name}`
    },
    formatStat(val) {
      if (val === null || val === undefined || val === "") {
        return "-"
      }
      return val
    },
    parseProgress(val) {
      const n = parseFloat(val)
      return isNaN(n) ? 0 : n
    },
    loadProjects() {
      getProjectList().then((res) => {
        if (res.code != 1) {
          this.$msg(res.msg)
          return
        }
        const list = []
        const counts = { running: 0, pending: 0, done: 0 }
        res.data.forEach((org) => {
          if (!org.coordinate) return
          const parts = org.coordinate.split(",")
          if (parts.length < 2) return
          const lat = parseFloat(parts[0])
          const lng = parseFloat(parts[1])
          if (isNaN(lat) || isNaN(lng)) return
          const prov = findProvinceByLngLat(lng, lat)
          const icon = stateToIcon(org.state)
          if (org.state == 1) counts.running++
          else if (org.state == 2) counts.pending++
          else if (org.state == 3) counts.done++
          list.push({
            id: org.id,
            name: org.orgname,
            lng,
            lat,
            icon,
            provId: prov ? prov.id : null,
            provName: prov ? prov.name : "",
            complete_length_percent: org.complete_length_percent,
            complete_length: org.complete_length,
            complete_m2: org.complete_m2,
            complete_volume: org.complete_volume,
            complete_ash: org.complete_ash,
            raw: org,
          })
        })
        this.points = list
        this.stateCounts = counts
        this.$nextTick(() => this.syncLayers())
      })
    },
    onBgLoad() {
      this.syncLayers()
    },
    syncLayers() {
      const container = this.$refs.mapContainer
      const img = this.$refs.bgImage
      if (!container || !img) return
      this.displayRect = getDisplayRect(
        container.clientWidth,
        container.clientHeight,
        img.naturalWidth || MAP_W,
        img.naturalHeight || MAP_H
      )
    },
    applyTransform() {
      this.clampPan()
    },
    clampPan() {
      const container = this.$refs.mapContainer
      if (!container) return
      const cw = container.clientWidth
      const ch = container.clientHeight
      const headerH = 80
      const footerH = 60
      const scaledW = cw * this.mapZoom
      const scaledH = ch * this.mapZoom
      if (scaledW <= cw) {
        this.mapPanX = Math.max(0, Math.min(cw - scaledW, this.mapPanX))
      } else {
        this.mapPanX = Math.min(0, Math.max(cw - scaledW, this.mapPanX))
      }
      if (scaledH <= ch) {
        this.mapPanY = Math.max(0, Math.min(ch - scaledH, this.mapPanY))
      } else {
        this.mapPanY = Math.min(headerH, Math.max(ch - scaledH - footerH, this.mapPanY))
      }
    },
    onWheel(e) {
      const container = this.$refs.mapContainer
      if (!container) return
      const rect = container.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const wx = (mx - this.mapPanX) / this.mapZoom
      const wy = (my - this.mapPanY) / this.mapZoom
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.mapZoom * delta))
      this.mapPanX = mx - wx * newZoom
      this.mapPanY = my - wy * newZoom
      this.mapZoom = newZoom
      this.applyTransform()
    },
    onMouseDown(e) {
      if (e.button !== 0) return
      e.preventDefault()
      this.hideMarkerInfo()
      this.isDragging = true
      this.didDrag = false
      this.stitchHoverId = null
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.panStartX = this.mapPanX
      this.panStartY = this.mapPanY
    },
    onMouseMove(e) {
      if (!this.isDragging) return
      const dx = e.clientX - this.dragStartX
      const dy = e.clientY - this.dragStartY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.didDrag = true
      this.mapPanX = this.panStartX + dx
      this.mapPanY = this.panStartY + dy
      this.applyTransform()
    },
    onMouseUp() {
      this.isDragging = false
    },
    onStitchImageLoad(e, provId) {
      const img = e.target
      if (!img || !img.naturalWidth) return
      try {
        const canvas = document.createElement("canvas")
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0)
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
        this.$set(this.stitchAlphaData, provId, {
          w: canvas.width,
          h: canvas.height,
          data: data.data,
        })
      } catch (err) {
        this.alphaLoadFailed = true
      }
    },
    onStitchMouseMove(e) {
      if (this.isDragging || this.markerHoverProvId) {
        if (this.stitchHoverId) this.stitchHoverId = null
        return
      }
      let target = e.target
      while (target && target !== e.currentTarget) {
        if (target.classList && target.classList.contains("map-marker")) {
          if (this.stitchHoverId) this.stitchHoverId = null
          return
        }
        target = target.parentNode
      }
      const container = this.$refs.mapContainer
      const layer = this.$refs.stitchLayer
      if (!container || !layer) return

      const rect = container.getBoundingClientRect()
      let mx = (e.clientX - rect.left - this.mapPanX) / this.mapZoom
      let my = (e.clientY - rect.top - this.mapPanY) / this.mapZoom
      mx -= layer.offsetLeft
      my -= layer.offsetTop

      const imgs = layer.querySelectorAll(".stitch-img")
      const useAlpha =
        !this.alphaLoadFailed &&
        Object.keys(this.stitchAlphaData).length > 0
      const candidates = []

      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i]
        const il = img.offsetLeft
        const it = img.offsetTop
        const iw = img.offsetWidth
        const ih = img.offsetHeight
        if (mx >= il && mx < il + iw && my >= it && my < it + ih) {
          candidates.push({
            img,
            pid: img.dataset.provId || img.id.replace("stitch-", ""),
            area: iw * ih,
          })
        }
      }
      candidates.sort((a, b) => a.area - b.area)

      let hitId = null
      for (let i = 0; i < candidates.length; i++) {
        const c = candidates[i]
        if (useAlpha) {
          const alpha = this.stitchAlphaData[c.pid]
          if (!alpha) continue
          const il = c.img.offsetLeft
          const it = c.img.offsetTop
          const iw = c.img.offsetWidth
          const ih = c.img.offsetHeight
          const rx = mx - il
          const ry = my - it
          const sx = Math.floor((rx / iw) * alpha.w)
          const sy = Math.floor((ry / ih) * alpha.h)
          const idx = (sy * alpha.w + sx) * 4 + 3
          if (alpha.data[idx] > 30) {
            hitId = c.pid
            break
          }
        } else {
          hitId = c.pid
          break
        }
      }
      this.stitchHoverId = hitId
    },
    onStitchMouseLeave() {
      this.stitchHoverId = null
    },
    onMapClick() {
      this.hideMarkerInfo()
    },
    onMarkerEnter(marker, e) {
      this.hoveredMarkerKey = marker.key
      this.markerHoverProvId = marker.point.provId
      this.stitchHoverId = null
      this.showMarkerInfo(marker, e.currentTarget)
    },
    onMarkerLeave() {
      this.hoveredMarkerKey = null
      this.markerHoverProvId = null
      this.hideMarkerInfo()
    },
    onMarkerClick(marker) {
      if (this.didDrag) return
      setProjectList(marker.point.id).then((res) => {
        if (res.code == 1) {
          localStorage.removeItem("navIds")
          localStorage.removeItem("navTags")
          localStorage.removeItem("actMenu")
          localStorage.setItem("home_page", res.data || '')
          this.$router.push({ path: '/home' })
        } else {
          this.$msg(res.msg)
        }
      })
    },
    showMarkerInfo(marker, markerEl) {
      const container = this.$refs.mapContainer
      if (!container || !markerEl) return

      const containerRect = container.getBoundingClientRect()
      const markerRect = markerEl.getBoundingClientRect()
      const dotX = (markerRect.left + markerRect.right) / 2 - containerRect.left
      const dotY = markerRect.bottom - containerRect.top
      const p = marker.point

      this.infoData = {
        name: p.name,
        progress: this.parseProgress(p.complete_length_percent),
        doneLen: this.formatStat(p.complete_length),
        doneM2: this.formatStat(p.complete_m2),
        grout: this.formatStat(p.complete_volume),
        ash: this.formatStat(p.complete_ash),
      }
      this.infoStyle = {
        left: `${dotX + 20}px`,
        top: `${dotY}px`,
        transform: "translateY(-100%)",
      }
      this.infoVisible = true
      this.$nextTick(() => this.renderGauge())
    },
    hideMarkerInfo() {
      this.infoVisible = false
      this.disposeGauge()
    },
    disposeGauge() {
      if (this.infoGaugeChart) {
        this.infoGaugeChart.dispose()
        this.infoGaugeChart = null
      }
    },
    infoArcColor(t) {
      t = Math.max(0, Math.min(1, t))
      for (let k = 1; k < INFO_ARC_STOPS.length; k++) {
        if (t <= INFO_ARC_STOPS[k].pos) {
          const a = INFO_ARC_STOPS[k - 1]
          const b = INFO_ARC_STOPS[k]
          const f = (t - a.pos) / (b.pos - a.pos)
          return `rgb(${Math.round(a.r + (b.r - a.r) * f)},${Math.round(a.g + (b.g - a.g) * f)},${Math.round(a.b + (b.b - a.b) * f)})`
        }
      }
      const last = INFO_ARC_STOPS[INFO_ARC_STOPS.length - 1]
      return `rgb(${last.r},${last.g},${last.b})`
    },
    infoArcColors(value) {
      const v = value / 100
      if (v <= 0) return [[1, "#e8eef5"]]
      const colors = []
      for (let i = 0; i <= 200; i++) {
        const t = i / 200
        colors.push([t * v, this.infoArcColor(t)])
      }
      if (v < 1) colors.push([1, "transparent"])
      return colors
    },
    infoGaugeLayer(lw, color, value) {
      return {
        type: "gauge",
        startAngle: 225,
        endAngle: -45,
        radius: "85%",
        center: ["50%", "55%"],
        min: 0,
        max: 100,
        pointer: { show: false },
        axisLine: { lineStyle: { width: lw, color }, roundCap: true },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: { show: false },
        data: [{ value }],
      }
    },
    renderGauge() {
      const el = this.$refs.gaugeChart
      if (!el) return
      this.disposeGauge()
      const progress = this.infoData.progress || 0
      this.infoGaugeChart = echarts.init(el)
      this.infoGaugeChart.setOption({
        series: [
          this.infoGaugeLayer(8, [[1, "#e8eef5"]], 0),
          this.infoGaugeLayer(8, this.infoArcColors(progress), progress),
        ],
      })
    },
  },
}
</script>

<style scoped>
.new-map-page {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: url("/new-map/images/bg.png") center center / cover no-repeat;
  font-family: -apple-system, "Microsoft YaHei", sans-serif;
  color: #e0f0ff;
}

.new-map-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  background: url("/new-map/images/header.png") center top / cover no-repeat;
  height: 80px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  letter-spacing: 4px;
}

.new-map-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  pointer-events: none;
}

.new-map-footer img {
  width: 100%;
  display: block;
}

.status-panel {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 60;
  width: 340px;
  background: rgba(235, 245, 255, 0.97);
  border: 1px solid rgba(179, 210, 255, 0.6);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(0, 80, 200, 0.1);
}

.sp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 0 16px;
}

.sp-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: bold;
  color: #1a5cb8;
}

.sp-title img {
  height: 20px;
}

.sp-stripe img {
  width: 66px;
  height: 16px;
  opacity: 0.85;
  display: block;
  position: relative;
  top: 16px;
}

.sp-divider {
  width: 100%;
  display: block;
}

.sp-cards {
  display: flex;
  gap: 10px;
  padding: 14px;
}

.sp-card {
  flex: 1;
  border-radius: 10px;
  background: rgba(225, 238, 255, 0.7);
  border: 1px solid rgba(140, 195, 255, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 6px 12px;
  gap: 10px;
}

.sp-card img {
  height: 68px;
}

.sp-card span {
  font-size: 13px;
  color: #1a4a8a;
  font-weight: 500;
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  cursor: grab;
}

.map-container.dragging {
  cursor: grabbing;
}

.map-world {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform-origin: 0 0;
}

.bg-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
}

.stitch-layer,
.markers-layer {
  position: absolute;
  pointer-events: none;
}

.stitch-img {
  position: absolute;
  opacity: 0;
  transition: opacity 0.3s, filter 0.3s;
  pointer-events: none;
}

.stitch-img.hover {
  opacity: 1;
  filter: brightness(1.12) sepia(0.35) saturate(1.8) hue-rotate(8deg);
}

.stitch-img.active {
  opacity: 1;
  filter: brightness(1.22) sepia(1) saturate(4.2) hue-rotate(-8deg);
}

.map-marker:hover {
  transform: translate(-50%, -100%) scale(calc(var(--inv-zoom, 1) * 1.2));
}

.map-marker:hover .pulse-dot {
  background: #ff570b;
  box-shadow: 0 0 4px #ff570b;
}

.map-marker:hover .pulse-ring {
  background: #ff570b;
}

.map-marker {
  position: absolute;
  pointer-events: auto;
  cursor: pointer;
  transform: translate(-50%, -100%) scale(var(--inv-zoom, 1));
  transform-origin: 50% 100%;
}

.map-marker img {
  height: 55px;
  display: block;
  position: relative;
  top: -4px;
}

.m-label-bottom {
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  margin-top: 10px;
  white-space: nowrap;
  font-size: 13px;
  color: #fff;
  font-weight: bold;
  text-shadow: 0 0 6px rgba(0, 0, 0, 0.9);
  pointer-events: none;
}

.pulse-wrapper {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(calc(-50% + 1px), calc(50% + 3px)) rotateX(60deg);
  width: 0;
  height: 0;
  pointer-events: none;
}

.pulse-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--pulse-color);
  opacity: 0.45;
  animation: pulse-spread 2s ease-out infinite;
}

.pulse-ring:nth-child(2) {
  animation-delay: 0.7s;
}

.pulse-ring:nth-child(3) {
  animation-delay: 1.4s;
}

.pulse-dot {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--pulse-color);
  opacity: 0.9;
  box-shadow: 0 0 4px var(--pulse-color);
}

@keyframes pulse-spread {
  0% {
    width: 12px;
    height: 12px;
    opacity: 0.7;
  }

  100% {
    width: 60px;
    height: 60px;
    opacity: 0;
  }
}

.marker-info {
  position: absolute;
  z-index: 100;
  width: 330px;
  height: 130px;
  background: url("/new-map/images/info.png") center/100% 100% no-repeat;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  padding: 10px 12px 10px 14px;
  box-sizing: border-box;
}

.mi-title {
  flex-shrink: 0;
  height: 30px;
  line-height: 30px;
  background: url("/new-map/images/11.png") left center/100% 100% no-repeat;
  padding: 0 10px;
  margin-left: 4px;
  transform: translate(5px, -5px);
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  white-space: nowrap;
}

.mi-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mi-gauge {
  flex: 0 0 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.mi-gauge-wrap {
  position: relative;
  width: 65px;
  height: 65px;
}

.mi-gauge-ec {
  width: 65px;
  height: 65px;
  position: absolute;
  top: 0;
  left: 0;
}

.mi-gauge-val {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: bold;
  color: #222;
}

.mi-gauge-label {
  font-size: 9px;
  color: #555;
}

.mi-stats {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mi-stat-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
}

.mi-stat {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.mi-stat img {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}

.mi-stat-val {
  font-size: 11px;
  font-weight: bold;
  display: block;
}

.mi-stat-val.orange {
  color: #ff7a00;
}

.mi-stat-val.green {
  color: #00b96b;
}

.mi-stat-val.blue,
.mi-stat-val.purple {
  color: #333;
}

.mi-stat-desc {
  font-size: 9px;
  color: #555;
  display: block;
}
</style>
